import React, { createContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getAccountId } from "../utilities/authHelper";
import DashboardSkeleton from "./DashboardSkeleton";
import { createTabs } from "./Sidebar/SidebarUtils";
import logoNew from "../../assets/logo-new.png";
import {
  fetchUserData,
  handleAddAccountId,
  useFetchSubscriptions,
  useInactivityTimer,
} from "../utilities/authUtilities";
import LoadingComponent from "./Common/LoadingComponent";

export const UserDashboardContext = createContext();

const ProtectedRoute = ({
  children,
  isAuthenticated = false,
  setTabs,
  tabs,
  setTabsLoaded = () => {},
  tabsLoaded = true,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [_, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [subscriptions, setSubscriptions] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const location = useLocation();

  const accountId = getAccountId();
  // ProtectedRoute re-renders on every navigation; a stable value keeps
  // context consumers in kept-alive tabs from re-rendering with it.
  const dashboardContextValue = useMemo(() => ({ tabs }), [tabs]);

  useEffect(() => {
    if (isAuthenticated) {
      setError(false);
      fetchUserData(setLoading, setUserProfile, setError);
      return;
    }

    setLoading(false);
    setError(false);
    setSubscriptions(null);
    setSubscriptionLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !subscriptionLoading && !tabsLoaded) {
      createTabs(setTabs, setTabsLoaded);
    }
  }, [isAuthenticated, subscriptionLoading, setTabs, setTabsLoaded, tabsLoaded]);

  useInactivityTimer(isAuthenticated);
  useFetchSubscriptions(
    setSubscriptions,
    setSubscriptionLoading,
    isAuthenticated,
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (error) {
    return <Navigate to="/logout" state={{ from: location }} replace />;
  }

  if (loading || subscriptionLoading || (subscriptions?.length > 0 && !tabsLoaded)) {
    return <DashboardSkeleton />;
  }

  return subscriptions?.length > 0 ? (
    <UserDashboardContext.Provider value={dashboardContextValue}>
      {children}
    </UserDashboardContext.Provider>
  ) : (
    <SubscriptionCheck accountId={accountId} />
  );
};

export default ProtectedRoute;

const SubscriptionCheck = ({ accountId }) => {
  const [accountIdInput, setAccountIdInput] = useState("");
  const [status, setStatus] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = () => {
    window.location.href = "/logout";
  };

  const handleSubmit = () => {
    handleAddAccountId(accountIdInput, setStatus, setError, setIsLoading);
  };

  return (
    <div className="w-100 h-100 d-flex flex-column">
      {/* Top Nav Bar */}
      <nav className="navbar navbar-light bg-light border-bottom">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <a className="navbar-brand mb-0" href="/">
            <img
              src={logoNew}
              alt="EMAlpha"
              className="mr-2"
              style={{ verticalAlign: "middle" }}
            />
          </a>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={handleLogout}
            style={{ borderRadius: "5px" }}
          >
            Logout
          </button>
        </div>
      </nav>
      {isLoading ? (
        <LoadingComponent />
      ) : (
        <>
          <div className="flex-grow-1 d-flex justify-content-center align-items-center p-4">
            <div
              className="text-center p-4"
              style={{
                maxWidth: "400px",
                backgroundColor: "#ffffff",
                borderRadius: "10px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              }}
            >
              <h2 className="mb-4">Subscription </h2>
              {status ? (
                <div
                  className="alert alert-success"
                  style={{
                    fontSize: "1rem",
                    padding: "1rem",
                    borderRadius: "8px",
                    boxShadow: "0px 2px 6px rgba(0, 128, 0, 0.2)",
                  }}
                  role="alert"
                >
                  <h5 className="mb-3">Request Submitted!</h5>
                  <p>
                    Your request has been sent to the account manager. Please
                    wait for their approval.
                  </p>
                </div>
              ) : (
                <>
                  {" "}
                  {error ? (
                    <>
                      <div
                        className="alert alert-danger"
                        style={{
                          fontSize: "1rem",
                          padding: "1rem",
                          borderRadius: "8px",
                          boxShadow: "0px 2px 6px rgba(0, 128, 0, 0.2)",
                        }}
                        role="alert"
                      >
                        <h5 className="mb-3">Error!</h5>
                        <p>Could not find the Account !</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <>
                        <p className="alert alert-warning" role="alert">
                          We couldn't find an active subscription associated
                          with your account.
                        </p>
                        {!accountId ? (
                          <>
                            <p className="alert alert-info mt-3" role="alert">
                              Have the Account ID?
                            </p>
                            <input
                              type="text"
                              className="form-control mb-3"
                              placeholder="Enter your Account ID"
                              value={accountIdInput}
                              onChange={(e) =>
                                setAccountIdInput(e.target.value)
                              }
                              style={{ borderRadius: "5px" }}
                            />
                            <button
                              className="btn btn-primary w-100"
                              style={{ borderRadius: "5px" }}
                              onClick={handleSubmit}
                            >
                              Submit
                            </button>
                          </>
                        ) : null}
                        <p
                          className="text-muted mt-3"
                          style={{ fontSize: "0.9rem" }}
                        >
                          If you don't have an Account ID, please contact your
                          system administrator. Or you can contact EMAlpha
                          Admin.
                        </p>
                      </>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
